import React, { useState, useMemo } from "react";
import { useQuery, useMutation } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import StarIcon from "@material-ui/icons/Star";
import RateReviewIcon from "@material-ui/icons/RateReview";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import MarkunreadOutlinedIcon from "@material-ui/icons/MarkunreadOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ReceiptIcon from "@material-ui/icons/Receipt";
import DeleteIcon from "@material-ui/icons/Delete";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import OrderDetails from "shopAdminViews/orders/OrderDetails";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import Button from "designV2/components/Button";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import DialogShell from "designV2/components/DialogShell";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import Reply from "./Reply";
import "./reviews.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * List + count combined into one round trip, the same way the other
 * redesigned screens do it. Filtering, sorting and pagination all run
 * client-side over one fetched window (a shop's reviews run in the
 * hundreds, not the thousands) — the same approach as Conversations.
 * ------------------------------------------------------------------ */

const REVIEW_FIELDS = `
  _id
  userComment
  readyComments {
    rate
    readyComment {
      _id
      type
    }
  }
  shopAdminReply {
    comment {
      _id
      userComment
      createdAt
    }
  }
  user {
    _id
    fullName
    phoneNumber
  }
  order {
    _id
    status
    finalPrice
    createdAt
    user {
      _id
      fullName
      phoneNumber
    }
    tracking {
      trackId
    }
    cart {
      products {
        quantity
        product {
          _id
          title
        }
      }
    }
  }
  createdAt
`;

const FETCH_LIMIT = 1000;

const GET_REVIEWS = gql`
  query getShopAdminCommentsOnShop($limit: Int, $skip: Int) {
    getShopAdminCommentsOnShop(pagination: { limit: $limit, skip: $skip }, sort: { createdAt: -1 }) {
      ${REVIEW_FIELDS}
    }
    getCommentsOnShopByShopAdminCount
  }
`;

const GET_RATING_CATEGORIES = gql`
  query getShopReadyComments($limit: Int, $skip: Int) {
    getShopReadyComments(pagination: { limit: $limit, skip: $skip }) {
      _id
      type
    }
  }
`;

const DELETE_REVIEW = gql`
  mutation removeCommentOnShopByShopAdmin($id: ID!) {
    removeCommentOnShopByShopAdmin(id: $id) {
      message
    }
  }
`;

/* ------------------------------------------------------------------ *
 * Constants + helpers
 * ------------------------------------------------------------------ */

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "ratingHigh", label: "Rating high-low" },
  { value: "ratingLow", label: "Rating low-high" },
];
const SORT_LABELS = SORT_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

// A review's own "rate" is really an average of however many rating
// categories the customer scored — there's no single overall number on
// the record itself. Reviews without any category scores (a text-only
// comment) return null so callers can show "No rating" instead of 0.
const averageRate = (review) => {
  const rates = (review.readyComments || [])
    .map((rc) => rc.rate)
    .filter((rate) => typeof rate === "number");
  if (rates.length === 0) return null;
  return rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
};

const rateTone = (avg) => {
  if (avg === null) return "neutral";
  if (avg >= 4) return "success";
  if (avg >= 2.5) return "warning";
  return "danger";
};

const hasReply = (review) => Boolean(review.shopAdminReply && review.shopAdminReply.comment);

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const readableError = (error) => {
  if (!error) return "";
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors[0].message;
  }
  return error.message || "Something went wrong, please try again.";
};

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Reviews = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [ratingFrom, setRatingFrom] = useState("");
  const [ratingTo, setRatingTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [openReview, setOpenReview] = useState(null);
  const [orderDetailTarget, setOrderDetailTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionError, setActionError] = useState("");

  const { data, loading, error, refetch } = useQuery(GET_REVIEWS, {
    variables: { limit: FETCH_LIMIT, skip: 0 },
    fetchPolicy: "network-only",
  });

  const { data: categoriesData } = useQuery(GET_RATING_CATEGORIES, {
    variables: { limit: 100, skip: 0 },
  });

  const [removeReview, { loading: deleting }] = useMutation(DELETE_REVIEW);

  const reviews = (data && data.getShopAdminCommentsOnShop) || [];
  const totalCount = (data && data.getCommentsOnShopByShopAdminCount) || 0;
  const categories = (categoriesData && categoriesData.getShopReadyComments) || [];

  /* ---------------- derived list ---------------- */

  const filteredReviews = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const customerTerm = customerFilter.trim().toLowerCase();
    const from = ratingFrom ? parseFloat(ratingFrom) : null;
    const to = ratingTo ? parseFloat(ratingTo) : null;
    const fromDate = dateFrom ? moment(dateFrom) : null;
    const toDate = dateTo ? moment(dateTo) : null;

    const filtered = reviews.filter((review) => {
      if (term && !String(review.userComment || "").toLowerCase().includes(term)) return false;
      if (customerTerm && !String((review.user && review.user.fullName) || "").toLowerCase().includes(customerTerm)) {
        return false;
      }
      if (statusFilter === "replied" && !hasReply(review)) return false;
      if (statusFilter === "unreplied" && hasReply(review)) return false;
      if (categoryFilter && !(review.readyComments || []).some((rc) => rc.readyComment && rc.readyComment._id === categoryFilter)) {
        return false;
      }
      const avg = averageRate(review);
      if (from !== null && (avg === null || avg < from)) return false;
      if (to !== null && (avg === null || avg > to)) return false;
      if (fromDate && !moment(review.createdAt).isSameOrAfter(fromDate, "day")) return false;
      if (toDate && !moment(review.createdAt).isSameOrBefore(toDate, "day")) return false;
      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      switch (sortOption) {
        case "oldest":
          return new Date(a.createdAt) - new Date(b.createdAt);
        case "ratingHigh":
          return (averageRate(b) ?? -1) - (averageRate(a) ?? -1);
        case "ratingLow":
          return (averageRate(a) ?? 6) - (averageRate(b) ?? 6);
        case "newest":
        default:
          return new Date(b.createdAt) - new Date(a.createdAt);
      }
    });

    return sorted;
  }, [reviews, searchTerm, customerFilter, statusFilter, categoryFilter, ratingFrom, ratingTo, dateFrom, dateTo, sortOption]);

  const pageStart = pageCount * perPage;
  const currentPageReviews = filteredReviews.slice(pageStart, pageStart + perPage);

  /* ---------------- metrics ---------------- */

  const ratedReviews = reviews.filter((r) => averageRate(r) !== null);
  const overallAverage =
    ratedReviews.length > 0
      ? ratedReviews.reduce((sum, r) => sum + averageRate(r), 0) / ratedReviews.length
      : null;
  const unrepliedCount = reviews.filter((r) => !hasReply(r)).length;
  const needsAttentionCount = reviews.filter((r) => {
    const avg = averageRate(r);
    return avg !== null && avg <= 2;
  }).length;

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (searchTerm.trim()) activeFilterChips.push({ id: "search", label: `Comment: ${searchTerm}` });
  if (customerFilter.trim()) activeFilterChips.push({ id: "customer", label: `Customer: ${customerFilter}` });
  if (statusFilter) {
    activeFilterChips.push({ id: "status", label: `Status: ${statusFilter === "replied" ? "Replied" : "Unreplied"}` });
  }
  if (categoryFilter) {
    const cat = categories.find((c) => c._id === categoryFilter);
    activeFilterChips.push({ id: "category", label: `Rated on: ${cat ? cat.type : categoryFilter}` });
  }
  if (ratingFrom || ratingTo) {
    activeFilterChips.push({ id: "rating", label: `Rating: ${ratingFrom || "1"} to ${ratingTo || "5"}` });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "search") setSearchTerm("");
    if (id === "customer") setCustomerFilter("");
    if (id === "status") setStatusFilter("");
    if (id === "category") setCategoryFilter("");
    if (id === "rating") {
      setRatingFrom("");
      setRatingTo("");
    }
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
    setPageCount(0);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setCustomerFilter("");
    setStatusFilter("");
    setCategoryFilter("");
    setRatingFrom("");
    setRatingTo("");
    setDateFrom("");
    setDateTo("");
    setPageCount(0);
  };

  /* ---------------- delete ---------------- */

  const handleDelete = () => {
    if (!deleteTarget || deleting) return;
    removeReview({ variables: { id: deleteTarget._id } })
      .then(() => {
        setDeleteTarget(null);
        if (openReview && openReview._id === deleteTarget._id) setOpenReview(null);
        return refetch();
      })
      .catch((err) => {
        setDeleteTarget(null);
        setActionError(readableError(err));
      });
  };

  /* ---------------- export ---------------- */

  const handleExport = () => {
    const rows = [["Customer", "Comment", "Average rating", "Status", "Created"]];
    filteredReviews.forEach((review) => {
      const avg = averageRate(review);
      rows.push([
        (review.user && review.user.fullName) || "-",
        review.userComment || "",
        avg === null ? "-" : avg.toFixed(1),
        hasReply(review) ? "Replied" : "Unreplied",
        moment(review.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Reviews");
    XLSX.writeFile(wb, "Reviews.xlsx");
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page reviewsPage">
        <main className="main">
          <PageHeader title="Reviews" description="What customers are saying about your shop." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={5} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page reviewsPage">
      <main className="main">
        {/* HEADER */}
        <PageHeader
          title="Reviews"
          count={totalCount}
          description="What customers are saying about your shop, and which ones still need a reply."
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label="Export"
              info={`${filteredReviews.length} review${filteredReviews.length === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={filteredReviews.length === 0}
            />
          }
        />

        {/* STATS */}
        <section className="stats">
          <MetricCard icon={<RateReviewIcon />} title="Total reviews" value={totalCount} />
          <MetricCard
            icon={<StarIcon />}
            title="Average rating"
            value={overallAverage === null ? "-" : overallAverage.toFixed(1)}
            tone={overallAverage === null ? "default" : rateTone(overallAverage)}
          />
          <MetricCard
            icon={<MarkunreadOutlinedIcon />}
            title="Unreplied"
            value={unrepliedCount}
            tone={unrepliedCount > 0 ? "warning" : "default"}
          />
          <MetricCard
            icon={<ReportProblemIcon />}
            title="Needs attention"
            value={needsAttentionCount}
            tone={needsAttentionCount > 0 ? "danger" : "default"}
            hint="2 stars or below"
          />
        </section>

        {/* FILTERS */}
        <section className="filters">
          <SearchField
            placeholder="Search review text..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPageCount(0);
            }}
          />
          <div className="filtersSelects">
            <SearchField
              placeholder="Customer name..."
              value={customerFilter}
              onChange={(e) => {
                setCustomerFilter(e.target.value);
                setPageCount(0);
              }}
            />
            <FilterDropdown
              label={statusFilter ? `Status: ${statusFilter === "replied" ? "Replied" : "Unreplied"}` : "Status"}
              options={[
                { value: "", label: "All statuses" },
                { value: "unreplied", label: "Unreplied" },
                { value: "replied", label: "Replied" },
              ]}
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageCount(0);
              }}
            />
            <FilterDropdown
              label={
                categoryFilter
                  ? `Rated on: ${(categories.find((c) => c._id === categoryFilter) || {}).type || ""}`
                  : "Rated on"
              }
              options={[
                { value: "", label: "Any category" },
                ...categories.map((c) => ({ value: c._id, label: c.type })),
              ]}
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPageCount(0);
              }}
            />
            <NumberRangeFilter
              label="Rating"
              fromValue={ratingFrom}
              toValue={ratingTo}
              onFromChange={(e) => {
                setRatingFrom(e.target.value);
                setPageCount(0);
              }}
              onToChange={(e) => {
                setRatingTo(e.target.value);
                setPageCount(0);
              }}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => {
                setDateFrom(e.target.value);
                setPageCount(0);
              }}
              onToChange={(e) => {
                setDateTo(e.target.value);
                setPageCount(0);
              }}
            />
            <FilterDropdown
              label={`Sort: ${SORT_LABELS[sortOption]}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList filters={activeFilterChips} onClearFilter={clearFilter} onClearAll={clearAllFilters} />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft" />
          <div className="tableTopBarRight">
            <IconButton className="tableTopIconButton" size="small" onClick={() => refetch()} title="Refresh reviews">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {actionError && (
          <div className="dsFormMessage error" role="alert">
            {actionError}
          </div>
        )}

        {/* TABLE */}
        <div className="tableResponsive">
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Review</div>
              <div className="tableHeaderCell">Rating</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Created</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {currentPageReviews.length === 0 ? (
              <EmptyState
                icon={<RateReviewIcon />}
                title={reviews.length === 0 ? "No reviews yet" : "No matches"}
                message={
                  reviews.length === 0
                    ? "Customer reviews will show up here once they start coming in."
                    : "No reviews match these filters. Try widening or clearing them."
                }
                action={reviews.length > 0 && <Button onClick={clearAllFilters}>Clear filters</Button>}
              />
            ) : (
              currentPageReviews.map((review) => {
                const avg = averageRate(review);
                const replied = hasReply(review);

                return (
                  <div className="row reviewRow" key={review._id} onClick={() => setOpenReview(review)}>
                    <div className="reviewCell" data-label="Review">
                      <h4>{(review.user && review.user.fullName) || "Unknown customer"}</h4>
                      <p>{review.userComment || "No written comment"}</p>
                    </div>

                    <span data-label="Rating">
                      {avg === null ? (
                        <span className="reviewNoRating">No rating</span>
                      ) : (
                        <span className={`reviewRating tone-${rateTone(avg)}`}>
                          <StarIcon fontSize="small" /> {avg.toFixed(1)}
                          <span className="reviewRatingCount">({review.readyComments.length})</span>
                        </span>
                      )}
                    </span>

                    <span data-label="Status">
                      <StatusBadge status={replied ? "success" : "warning"} label={replied ? "Replied" : "Unreplied"} />
                    </span>

                    <span className="updated" data-label="Created">
                      <strong>{formatDate(review.createdAt)}</strong>
                      <span>{formatTime(review.createdAt)}</span>
                    </span>

                    <div className="actions">
                      {review.order && (
                        <IconButton
                          size="small"
                          className="rowActionButton"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOrderDetailTarget(review.order);
                          }}
                          title="View order"
                        >
                          <ReceiptIcon fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(review);
                        }}
                        title="Delete review"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        className="rowActionButton"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenReview(review);
                        }}
                        title={replied ? "View" : "Reply"}
                      >
                        <ChevronRightIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                );
              })
            )}
          </section>
        </div>

        {/* PAGINATION */}
        {filteredReviews.length > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={filteredReviews.length}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      {/* REVIEW */}
      <DialogShell
        open={Boolean(openReview)}
        onClose={() => setOpenReview(null)}
        title={openReview ? (openReview.user && openReview.user.fullName) || "Review" : ""}
        subtitle={openReview ? formatDate(openReview.createdAt) : ""}
        size="lg"
      >
        {openReview && (
          <Reply
            review={openReview}
            onSent={() => refetch()}
            onViewOrder={(order) => setOrderDetailTarget(order)}
          />
        )}
      </DialogShell>

      {/* LINKED ORDER */}
      <DialogShell
        open={Boolean(orderDetailTarget)}
        onClose={() => setOrderDetailTarget(null)}
        title="Order details"
        subtitle={orderDetailTarget ? `#${orderDetailTarget._id.slice(-8).toUpperCase()}` : ""}
      >
        {orderDetailTarget && <OrderDetails order={orderDetailTarget} />}
      </DialogShell>

      {/* DELETE */}
      <DialogShell
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete review"
        size="sm"
        actions={[
          { label: "Cancel", onClick: () => setDeleteTarget(null) },
          { label: "Delete", onClick: handleDelete, variant: "danger", loading: deleting },
        ]}
      >
        {deleteTarget && (
          <div className="reviewConfirmBody">
            <span>
              Delete this review from <strong>{(deleteTarget.user && deleteTarget.user.fullName) || "this customer"}</strong>?
            </span>
            <span>This cannot be undone.</span>
          </div>
        )}
      </DialogShell>
    </div>
  );
};

export default Reviews;
